import * as React from 'react';

import {
  Button,
  ButtonTheme,
  ButtonThemeType,
  IStyleConfig, mergeStyles, Preloader, TextField, Tooltip} from '@cian/components';

import { isEmail } from '../../../../../utils/validators';
import { License } from '../license';

const styles = require('./subscription_form.css');

export enum SendStatus {
  INITIAL,
  REQUEST_PENDING,
  REQUEST_FAILED,
  SUCCESS,
}

enum ValidStatus {
  INITIAL,
  EMPTY,
  INVALID,
  VALID,
}

export interface ISubscriptionFormGeneralProps {
  email?: string;
  onSubmit: (email: string) => void;
  onReset?: () => void;
  inputStyle?: IStyleConfig;
  formStyle?: IStyleConfig;
  buttonTheme?: ButtonThemeType;
  buttonText?: React.ReactNode;
  successMessage?: React.ReactNode | (() => React.ReactNode);
  errorText?: string;
  allowRetry?: boolean;
  userIsAuthenticated?: boolean;
}

export interface ISubscriptionFormProps extends ISubscriptionFormGeneralProps {
  status: SendStatus;
}

const messages = {
  SERVICE_UNAVAILABLE: 'Сервер недоступен. Попробуйте еще раз.',
  PLACEHOLDER: 'Ваш email',
  INVALID: 'Введите корректный адрес',
  EMPTY: 'Введите адрес',
};

interface ISubscriptionFormState {
  email: string;
  errorText: string;
  validStatus: ValidStatus;
}

export default class SubscriptionForm extends React.Component<ISubscriptionFormProps, ISubscriptionFormState> {
  public static defaultProps = {
    status: SendStatus.INITIAL,
    onSubmit: () => {},
    allowRetry: true,
  };

  public constructor(props: ISubscriptionFormProps) {
    super(props);

    this.state = this._getInitialState();
  }

  public render() {
    const { validStatus, email = '' } = this.state;
    const { status, allowRetry, successMessage, userIsAuthenticated } = this.props;
    const isInvalid = validStatus === ValidStatus.INVALID;
    const isPending = status === SendStatus.REQUEST_PENDING;
    const isSuccess = status === SendStatus.SUCCESS;

    return (
      <form {...mergeStyles(styles['form'], this.props.formStyle)} onSubmit={(e) => this.handleNativeSubmitEvent(e)}>
        {!isSuccess && (
          <fieldset {...mergeStyles(styles['row'])}>
            <div {...mergeStyles(styles['input'], this.props.inputStyle)}>
              <Tooltip tooltipContent={this.renderErrorText()}>
                <TextField
                  containerStyle={styles['input-container']}
                  placeholder={messages.PLACEHOLDER}
                  invalid={isInvalid}
                  disabled={isPending}
                  value={email}
                  onValueChange={(val) => this.setEmail(val)}
                />
              </Tooltip>
            </div>
            <Button
              theme={this.props.buttonTheme}
              buttonStyle={styles['button']}
              onClick={() => this.checkAndSendForm()}
              disabled={isPending}
            >
              {this.renderButtonContent()}
            </Button>
            {!userIsAuthenticated && <License />}
          </fieldset>
        )}
        {isSuccess && (
          <div className={styles['success']}>
            <i className={styles['success-checkmark']} />
            <p className={styles['success-text']}>
              {successMessage ? (
                typeof successMessage === 'function'
                  ? successMessage()
                  : successMessage
              ) : (
                <span>
                  На&nbsp;<strong>{email}</strong> отправлено письмо для&nbsp;активации подписки.
                </span>
              )}
              {allowRetry && (
                <span>
                  &nbsp;<Button
                    theme={ButtonTheme.TOGGLE}
                    buttonStyle={styles['retry-button']}
                    onClick={() => this.reset()}
                  >Выбрать другой адрес</Button>
                </span>
              )}
            </p>
          </div>
        )}
      </form>
    );
  }

  private _getInitialState(): ISubscriptionFormState {
    return {
      validStatus: ValidStatus.INITIAL,
      email: this.props.email,
      errorText: undefined,
    };
  }

  private renderButtonContent(): React.ReactNode {
    const isPending = this.props.status === SendStatus.REQUEST_PENDING;

    return (
      <div {...mergeStyles(
        styles['button-content'],
        isPending && styles['button-content_faded']
      )}>
        <div {...mergeStyles(
          styles['preloader'],
          isPending && styles['preloader_active']
        )}>
          <Preloader />
        </div>
        {this.props.buttonText !== undefined ? this.props.buttonText : 'Подписаться на обновления'}
      </div>
    );
  }

  private setEmail(email: string = '') {
    const { status } = this.props;
    let newStatus: ValidStatus;

    if (status === SendStatus.REQUEST_FAILED) {
      newStatus = ValidStatus.INITIAL;
    }

    this.setState({
      validStatus: newStatus,
      email,
    } as ISubscriptionFormState);
  }

  private validate(passEmpty: boolean = true) {
    const { email = '' } = this.state;
    const { status } = this.props;
    const isValid = isEmail(email);
    let newStatus: ValidStatus;

    if (status === SendStatus.REQUEST_PENDING) {
      return;
    }

    if (isValid) {
      newStatus = ValidStatus.VALID;
    } else if (email.length) {
      newStatus = ValidStatus.INVALID;
    } else if (passEmpty) {
      newStatus = ValidStatus.INITIAL;
    } else {
      newStatus = ValidStatus.EMPTY;
    }

    this.setState({
      validStatus: newStatus,
      email,
    } as ISubscriptionFormState, isValid && this.submit);
  }

  private submit() {
    this.props.onSubmit(this.state.email);
  }

  private reset() {
    this.setState(this._getInitialState(), this.props.onReset);
  }

  private checkAndSendForm() {
    this.validate(false);
  }

  private handleNativeSubmitEvent(event: React.FormEvent<HTMLElement>) {
    event.preventDefault();
    this.checkAndSendForm();
  }

  private renderErrorText() {
    const { validStatus } = this.state;
    const { status, errorText = messages.SERVICE_UNAVAILABLE } = this.props;
    if (status === SendStatus.REQUEST_FAILED) {
      return errorText;
    }

    if (validStatus === ValidStatus.INVALID) {
      return messages.INVALID;
    }

    if (validStatus === ValidStatus.EMPTY ) {
      return messages.EMPTY;
    }

    return null;
  }
}



// WEBPACK FOOTER //
// ./src/bundles/common/components/subscription_form/component/subscription_form.tsx